import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaV1Service } from '../service';
import { GetCallPathIO } from '../types';

export const getGetCallPathQueryKey = (req: GetCallPathIO['input']) => ['V1', 'GetCallPath', req] as const;
type QueryKey = ReturnType<typeof getGetCallPathQueryKey>;

/**
 * A hook that returns a query for the `GetCallPath` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetCallPathQuery = <E = unknown, D = GetCallPathIO['output']>(
  req: GetCallPathIO['input'],
  options?: LimitedSchemaQueryOptions<GetCallPathIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetCallPathIO['output'], E, D, QueryKey>({
    queryKey: getGetCallPathQueryKey(req),
    queryFn: () => SchemaV1Service.GetCallPath(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetCallPath` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetCallPathQueries = <E = unknown, D = GetCallPathIO['output']>(
  argsArr: {
    req: GetCallPathIO['input'];
    options?: LimitedSchemaQueryOptions<GetCallPathIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetCallPathQueryKey(req),
      queryFn: () => SchemaV1Service.GetCallPath(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetCallPathQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetCallPath` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetCallPath = () => {
  const queryClient = useQueryClient();

  const fetchGetCallPath = async <E = unknown, D = GetCallPathIO['output']>(
    req: GetCallPathIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetCallPathIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetCallPathIO['output'], E, D, QueryKey>(
      getGetCallPathQueryKey(req),
      () => SchemaV1Service.GetCallPath(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetCallPath;
};
